import {
    FormControl,
    FormHelperText,
    InputAdornment as MuiInputAdornment,
    Slider as MuiSlider,
    TextField,
    FormLabel,
} from "@mui/material";
import { styled, css } from "styled-components";
import theme from "styled-theming";

import {
    styleBodyL,
    styleBodyM,
    styleBodyS,
} from "style/components/Typography";
import { MQ } from "style/mediaQueries";

const labelColor = theme("theme", {
    undefined: "--text-on-neutral-secondary",
    lightgray: "--text-on-neutral-secondary",
    blue: "--text-on-blue-secondary",
    cyan: "--text-on-cyan-secondary",
    black: "--text-on-blue-secondary",
});

const errorColor = theme("theme", {
    undefined: "--color-coral-d10",
    lightgray: "--color-coral-d10",
    blue: "--color-coral",
    cyan: "--color-coral-d10",
});

const textColor = theme("theme", {
    undefined: "--text-on-neutral-primary",
    lightgray: "--text-on-neutral-primary",
    blue: "--text-on-blue-primary",
    cyan: "--text-on-cyan-primary",
    black: "--text-on-blue-primary",
});

const textDisabled = theme("theme", {
    undefined: "--text-on-neutral-disabled",
    lightgray: "--text-on-neutral-disabled",
    blue: "--text-on-blue-disabled",
    cyan: "--text-on-cyan-disabled",
    black: "--text-on-blue-disabled",
});

const thumbColor = theme("theme", {
    undefined: "--color-blue",
    lightgray: "--color-blue",
    blue: "--color-cyan-l60",
    cyan: "--color-blue",
    black: "--color-cyan-l60",
});

const railColor = theme("theme", {
    undefined: "--color-black-o15",
    lightgray: "--color-black-o15",
    blue: "--color-cyan-l60-o20",
    cyan: "--color-blue-o15",
    black: "--color-cyan-l60-o20",
});

const sliderDisabledColor = theme("theme", {
    undefined: "--color-black",
    lightgray: "--color-black",
    blue: "--color-cyan-l60",
    cyan: "--color-blue",
    black: "--color-cyan-l60",
});

const borderHover = theme("theme", {
    undefined: "--color-cyan-l40",
    lightgray: "--color-cyan-l40",
    blue: "--color-cyan",
    cyan: "--color-cyan",
});

const borderFocus = "--field-focus";

const borderDisabled = theme("theme", {
    undefined: "--field-on-neutral-disabled-border",
    lightgray: "--field-on-neutral-disabled-border",
    blue: "--field-on-blue-disabled-border",
    cyan: "--field-on-cyan-disabled-border",
    black: "--field-on-blue-disabled-border",
});

export const Wrapper = styled(FormControl)`
    flex-direction: row;
    grid-column: col-start 1 / span 2;

    ${MQ.FROM_M} {
        grid-column: ${theme("displayOption", {
            Full: "auto / span 6",
            Half: "auto / span 3",
            OneThird: "auto / span 2",
            TwoThirds: "auto / span 4",
        })};
    }

    && {
        column-gap: 0.4375rem;
        display: grid;
        grid-template-columns: repeat(4, [col-start] 1fr);
        width: 100%;

        ${MQ.FROM_M} {
            column-gap: 0.875rem;
            grid-template-columns: repeat(12, [col-start] 1fr);
        }
    }
`;

export const Label = styled(FormLabel)`
    && {
        ${theme("size", {
            small: styleBodyM,
            medium: styleBodyL,
        })};
        color: var(${textColor});
        grid-column: col-start 1 / span 2;
        margin-bottom: 0.875rem;
        margin-top: ${theme("hasInput", { true: "0.4375rem", false: "0" })};

        ${MQ.FROM_M} {
            grid-column: col-start 1 / span 6;
        }

        &.Mui-focused {
            color: var(${textColor});
        }

        &.Mui-disabled {
            color: var(${textDisabled});
        }
    }
`;

export const InputAdornment = styled(MuiInputAdornment)`
    && {
        align-items: flex-end;
        margin-left: 0.25rem;
        margin-top: 0.6875rem;

        .MuiTypography-root {
            align-items: center;
            color: var(${textColor});
        }
    }
`;

export const Input = styled(TextField)`
    && {
        grid-column: col-start 3 / span 2;
        grid-row: 1;

        .MuiOutlinedInput {
            &-input {
                ${theme("size", {
                    small: styleBodyM,
                    medium: styleBodyL,
                })};
                display: block;
                padding: 0.4375rem;
                height: auto;
                text-align: right;
                color: var(${textColor});

                &.Mui-disabled {
                    -webkit-text-fill-color: var(${textDisabled});
                    color: var(${textDisabled});
                }
            }

            &-notchedOutline {
                border-color: var(${railColor});
                transition: border-color 0.2s ease-in-out;
            }

            &-root {
                margin-bottom: 9px;
                padding-right: 0;

                &:hover {
                    &:not(.Mui-disabled, .Mui-error) {
                        .MuiOutlinedInput {
                            &-notchedOutline {
                                border-color: var(${borderHover});
                            }
                        }
                    }
                }

                &.Mui-focused {
                    .MuiOutlinedInput {
                        &-notchedOutline {
                            border-color: var(${borderHover});
                            border-width: 1px;
                            box-shadow: 0 0 0 0.4375rem var(${borderFocus});
                        }
                    }
                }

                &.Mui-disabled {
                    .MuiOutlinedInput {
                        &-notchedOutline {
                            border-color: var(${borderDisabled});
                        }
                    }
                }

                .MuiInputAdornment {
                    &-root {
                        ${theme("size", {
                            small: styleBodyM,
                            medium: styleBodyL,
                        })};
                        color: var(${labelColor});
                        height: 100%;
                        margin: 0;
                        padding: 0.4375rem 0.4375rem 0.4375rem 0;
                        align-items: center;

                        p {
                            ${theme("size", {
                                small: styleBodyM,
                                medium: styleBodyL,
                            })};
                        }
                    }
                }
            }
        }

        ${MQ.FROM_M} {
            grid-column: col-start 7 / span 6;
        }
    }
`;

export const Value = styled.div<ValueProps>`
    ${theme("size", {
        small: styleBodyS,
        medium: styleBodyM,
    })};
    color: var(${textColor});
    grid-column: col-start 3 / span 2;
    grid-row: 1;
    margin-bottom: 0.875rem;
    margin-left: 0.875rem;
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;

    ${(props) =>
        props.disabled &&
        css`
            color: var(${textDisabled});
        `}

    ${MQ.FROM_M} {
        grid-column: col-start 7 / span 6;
    }
`;

export const StyledSlider = styled(MuiSlider)`
    && {
        color: var(${thumbColor});
        grid-column: col-start 1 / span 4;
        margin: 0 auto 0.4375rem;
        width: calc(100%);

        ${MQ.FROM_M} {
            grid-column: col-start 1 / span 12;
        }

        &.Mui-disabled {
            color: var(${sliderDisabledColor});

            .MuiSlider-track {
                display: none;
            }
        }

        .MuiSlider-thumb {
            width: 2.1875rem;
            height: 2.1875rem;

            &:hover,
            &.Mui-focusVisible {
                box-shadow: 0 0 0 7px var(${borderFocus});
            }

            &.Mui-disabled {
                display: none;
            }

            &::before {
                box-shadow: none;
            }
        }

        &.MuiSlider-dragging {
            .MuiSlider-thumb {
                box-shadow: 0 0 0 7px var(${borderFocus});
            }
        }

        .MuiSlider-track {
            height: 0.4375rem;
            border-radius: 4px;
        }

        .MuiSlider-markLabel {
            color: var(${labelColor});
        }

        .MuiSlider-rail {
            background-color: var(${railColor});
            opacity: 1;
            height: 0.4375rem;
            border-radius: 4px;
        }

        .MuiSlider-valueLabel {
            display: none;
        }
    }
`;

export const HelperText = styled(FormHelperText)`
    && {
        ${theme("size", {
            small: styleBodyS,
            medium: styleBodyM,
        })};
        color: var(${labelColor});
        grid-column: col-start 1 / span 4;
        letter-spacing: normal;
        margin-left: 0;
        margin-top: 0.4375rem;

        ${MQ.FROM_M} {
            grid-column: col-start 1 / span 12;
        }

        &.Mui-error {
            color: var(${errorColor});
        }

        &.Mui-disabled {
            color: var(${textDisabled});
        }
    }
`;

interface ValueProps {
    disabled?: boolean;
}
